import moment from 'moment';
import { birthdayOccurrenceIn } from '../../../utils/client-queue';
import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import { clientNameOf, reminderOf } from './clientPresentation';
import type { QueueClient } from '../../../types/client';

/**
 * The month grid behind the ClientV2 reminder calendar.
 *
 * Pure by design (spec D11): no React, no DOM, no ClientV2-only state. ClientV1
 * holds the same client array and can call this unchanged when it gets the
 * calendar.
 *
 * Deliberately NOT built on `isClientRemindMe` (utils/adviser-client.js:41):
 * that helper caps at one month ahead and reads the real clock itself, so a
 * calendar paged to December would come back empty and could not be tested at
 * a fixed instant. `reminderOf` takes `now` and returns the date whatever it is.
 */

export type CalendarEntry =
  | { kind: 'reminder'; client: QueueClient; mark: 'due' | 'upcoming' }
  | { kind: 'birthday'; client: QueueClient; age: number };

export type CalendarFilter = 'reminder' | 'birthday' | 'all';

export type CalendarCell = { date: moment.Moment; inMonth: boolean };

export type CalendarMonth = {
  /** 'YYYY-MM-DD' → that day's entries. Only days inside the month appear. */
  byDay: Map<string, CalendarEntry[]>;
  /** The 42 rendered cells in order, including the leading and trailing spill. */
  cells: CalendarCell[];
  reminderCount: number;
  birthdayCount: number;
};

const DAY_KEY = 'YYYY-MM-DD';

/** Six weeks, always — a fixed height stops the grid jumping between months. */
const CELL_COUNT = 42;

const buildCells = (month: moment.Moment): CalendarCell[] => {
  const firstOfMonth = month.clone().startOf('month');
  const gridStart = firstOfMonth.clone().startOf('week');
  const cells: CalendarCell[] = [];
  for (let index = 0; index < CELL_COUNT; index += 1) {
    const date = gridStart.clone().add(index, 'days');
    cells.push({ date, inMonth: date.isSame(firstOfMonth, 'month') });
  }
  return cells;
};

export const reminderCalendarMonth = (
  clients: QueueClient[],
  adviserEmail: string | undefined,
  month: moment.Moment,
  now: moment.Moment,
  filter: CalendarFilter
): CalendarMonth => {
  const byDay = new Map<string, CalendarEntry[]>();
  let reminderCount = 0;
  let birthdayCount = 0;

  const add = (key: string, entry: CalendarEntry): void => {
    const existing = byDay.get(key);
    if (existing) existing.push(entry);
    else byDay.set(key, [entry]);
  };

  const wantsReminders = filter !== 'birthday';
  const wantsBirthdays = filter !== 'reminder';

  clients.forEach(client => {
    if (wantsReminders) {
      // reminderOf reads the CONNECTION-GATED factsheet: a disconnected
      // client's reminder is withheld, exactly as the drawer and the today
      // queue withhold it (spec D7).
      const reminder = reminderOf(client, adviserEmail, now);
      if (reminder && reminder.date.isSame(month, 'month')) {
        add(reminder.date.format(DAY_KEY), {
          kind: 'reminder',
          client,
          mark: reminder.days <= 0 ? 'due' : 'upcoming'
        });
        reminderCount += 1;
      }
    }

    if (wantsBirthdays) {
      // The dob is ordinary adviser-owned data, NOT connection-gated — the
      // other half of D7. Reading it through the gated factsheet would hide
      // birthdays the adviser is entitled to see.
      const dob = adviserFactsheetOf(client, adviserEmail)?.dob;
      const occurrence = birthdayOccurrenceIn(dob, month.year());
      if (occurrence && occurrence.isSame(month, 'month')) {
        add(occurrence.format(DAY_KEY), {
          kind: 'birthday',
          client,
          age: occurrence.year() - moment.parseZone(dob as string).year()
        });
        birthdayCount += 1;
      }
    }
  });

  // Reminders before birthdays, then by the name the rows display, so a day's
  // list opens with what is actually asking to be done.
  byDay.forEach(entries =>
    entries.sort((left, right) => {
      if (left.kind !== right.kind) return left.kind === 'reminder' ? -1 : 1;
      return clientNameOf(left.client).localeCompare(clientNameOf(right.client));
    })
  );

  return { byDay, cells: buildCells(month), reminderCount, birthdayCount };
};

/**
 * Who already has a reminder on each day from today on — the load the
 * meeting-note date picker marks, so an adviser setting a reminder can see a
 * crowded day before picking it.
 *
 * Not month-bound like reminderCalendarMonth: the picker pages freely, and one
 * pass over the clients is cheaper than one per month shown. Past days are left
 * out because that picker's minDate is today. `excludeClientId` is the client
 * whose reminder is being edited — its saved date is about to change, and
 * counting it would report the day it is moving away from as busy.
 */
export const reminderLoadByDay = (
  clients: QueueClient[],
  adviserEmail: string | undefined,
  now: moment.Moment,
  excludeClientId?: string
): Map<string, QueueClient[]> => {
  const byDay = new Map<string, QueueClient[]>();
  clients.forEach(client => {
    if (excludeClientId && client._id === excludeClientId) return;
    const reminder = reminderOf(client, adviserEmail, now);
    if (!reminder || reminder.days < 0) return;
    const key = reminder.date.format(DAY_KEY);
    const existing = byDay.get(key);
    if (existing) existing.push(client);
    else byDay.set(key, [client]);
  });
  byDay.forEach(dayClients => dayClients.sort((left, right) => clientNameOf(left).localeCompare(clientNameOf(right))));
  return byDay;
};
